import React from 'react';
import type {
  GrowthDeltaDir,
  GrowthIconName,
  GrowthMetricDef,
  GrowthToneKey,
  GrowthValueLine,
} from '../../growth-dashboard/types';
import { Icon, type IconKind } from '../Icon/Icon';
import { GrowthIcon } from './GrowthIcon';

export const GROWTH_TONE: Record<
  GrowthToneKey,
  { accent: string; tint: string }
> = {
  ai: { accent: 'var(--ai-3)', tint: 'var(--ai-tint)' },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: { accent: 'var(--terracotta)', tint: 'color-mix(in srgb, var(--terracotta) 10%, transparent)' },
  lav: { accent: '#7A5CFF', tint: 'rgba(122,79,214,0.13)' },
};

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink-3)]';

export function GdCard({
  children,
  className = '',
  padding = true,
}: {
  children: React.ReactNode;
  className?: string;
  padding?: boolean;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        padding ? 'p-5' : '',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

export function GdSectionHead({
  title,
  sub,
  accent,
  icon,
  right,
}: {
  title: string;
  sub?: string;
  accent?: GrowthToneKey;
  icon?: IconKind;
  right?: React.ReactNode;
}) {
  const tone = GROWTH_TONE[accent ?? 'ai'];
  return (
    <div className="mb-3.5 flex items-end justify-between gap-4">
      <div className="flex items-center gap-[11px]">
        {icon ? (
          <span
            className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px]"
            style={{ background: tone.tint, color: tone.accent }}
          >
            <Icon kind={icon} size={16} stroke={1.9} />
          </span>
        ) : null}
        <div>
          <h2 className="m-0 font-[var(--font-display)] text-[19px] font-semibold tracking-[-0.025em] text-[var(--ink-0)]">
            {title}
          </h2>
          {sub ? <p className="m-0 mt-[3px] text-[12.5px] text-[var(--ink-3)]">{sub}</p> : null}
        </div>
      </div>
      {right}
    </div>
  );
}

function deltaColor(dir?: GrowthDeltaDir) {
  if (dir === 'up') return 'text-[var(--success)]';
  if (dir === 'down') return 'text-[var(--terracotta)]';
  return 'text-[var(--ink-3)]';
}

export function GdMetric({
  label,
  value,
  prefix,
  delta,
  deltaDir,
  deltaLabel,
  sub,
  accent,
  bar,
}: GrowthMetricDef) {
  return (
    <GdCard className="px-[18px] py-4">
      <div className={eyebrowClass}>{label}</div>
      <div className="mt-3 flex items-baseline gap-[3px]">
        {prefix ? (
          <span className="font-[var(--font-display)] text-xl font-semibold text-[var(--ink-3)]">
            {prefix}
          </span>
        ) : null}
        <span
          className="font-[var(--font-display)] text-[30px] font-extrabold leading-none tracking-[-0.04em] text-[var(--ink-0)]"
          style={accent ? { color: GROWTH_TONE[accent].accent } : undefined}
        >
          {value}
        </span>
      </div>
      {delta ? (
        <div className={`mt-2 flex items-center gap-1.5 text-[12.5px] font-semibold ${deltaColor(deltaDir)}`}>
          <span className="inline-flex items-center gap-0.5">
            {deltaDir === 'up' ? '▲' : deltaDir === 'down' ? '▼' : null}
            {delta}
          </span>
          {deltaLabel ? <span className="font-normal text-[var(--ink-4)]">{deltaLabel}</span> : null}
        </div>
      ) : null}
      {sub ? <div className="mt-2 text-[11.5px] text-[var(--ink-3)]">{sub}</div> : null}
      {bar !== undefined ? (
        <div className="mt-2 h-[5px] overflow-hidden rounded-[3px] bg-[var(--surface-mute)]">
          <div className="h-full rounded-[3px] bg-[var(--gold)]" style={{ width: `${bar}%` }} />
        </div>
      ) : null}
    </GdCard>
  );
}

export function GdValueCard({
  label,
  value,
  prefix,
  suffix,
  accent,
  icon,
  lines,
}: {
  label: string;
  value: string;
  prefix?: string;
  suffix?: string;
  accent: GrowthToneKey;
  icon: GrowthIconName;
  lines: GrowthValueLine[];
}) {
  const tone = GROWTH_TONE[accent];
  return (
    <GdCard className="px-5 py-[18px]">
      <div className="mb-3.5 flex items-center gap-2">
        <span
          className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg"
          style={{ background: tone.tint, color: tone.accent }}
        >
          <GrowthIcon kind={icon} size={15} stroke={2} />
        </span>
        <span className={eyebrowClass}>{label}</span>
      </div>
      <div className="flex items-baseline gap-1">
        {prefix ? (
          <span className="font-[var(--font-display)] text-[22px] font-semibold text-[var(--ink-3)]">
            {prefix}
          </span>
        ) : null}
        <span
          className="font-[var(--font-display)] text-[34px] font-extrabold leading-none tracking-[-0.04em]"
          style={{ color: tone.accent }}
        >
          {value}
        </span>
        {suffix ? <span className="text-[13px] text-[var(--ink-3)]">{suffix}</span> : null}
      </div>
      <div className="mt-3 flex flex-col gap-1.5">
        {lines.map((line, index) => (
          <div key={index} className="flex items-start gap-2 text-xs leading-normal text-[var(--ink-2)]">
            <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-[var(--cream-shadow)]" />
            <span className="flex-1">
              {line.t}{' '}
              {line.v ? <b className="font-semibold text-[var(--ink-0)]">{line.v}</b> : null}
            </span>
          </div>
        ))}
      </div>
    </GdCard>
  );
}
